import { all, AllEffect, call, ForkEffect, put, StrictEffect, takeEvery } from 'redux-saga/effects';
import {
  AutoLoginActionType,
  autoLoginUpdate,
  RefreshUserAction,
} from '../actions/autoLoginActions';
import { AutoLoginStep } from '@/models/enum/auto-login-step.enum';
import { AxiosResponse } from 'axios';
import { ApiResponse } from '@/models/api-response.model';
import { Jackpot } from '@lucky7ventures/lucky-components';
import { isAxiosResponse } from '@/utils/type-guards';
import { getLimits } from '../actions/limitsActions';
import { BUILD_MODE, IS_SWEDEN } from '@/constants/constants';
import { getLoginHistory } from '../actions/loginHistoryActions';
import { getUserPlayStatsAndLimits } from '../actions/userPlayStatsAndLimitsActions';
import { getConsents } from '../actions/consentsActions';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import CookiesManager from '../../shared/cookies-manager';
import { getUser, getUserTags } from '@/store/actions/user';
import { loginSuccess } from '@/store/actions/auth';
import { getBonuses, resetBonusesState } from '@/store/bonuses/actions';
import BffApiService from '@/shared/bffApiService';

export function* refreshUserSaga(
  action: RefreshUserAction,
): Generator<StrictEffect, void, unknown> {
  const token = action.token;
  yield put(resetBonusesState());
  yield put(getUser(token));
  yield put(getBonuses());
  yield put(getConsents());
  yield put(getLimits());
  yield put(getUserTags());
  if (IS_SWEDEN) {
    yield put(getLoginHistory());
    yield put(getUserPlayStatsAndLimits(12));
  }
  if (BUILD_MODE === 'canada') {
    yield put(getLoginHistory());
  }
}

export function* autoLoginSaga(): Generator<
  StrictEffect,
  void,
  AxiosResponse<ApiResponse<Jackpot[]>> | number
> {
  const token = CookiesManager.getTokenCookie();

  if (!token) {
    yield put(autoLoginUpdate(AutoLoginStep.Skipped));
    return;
  }

  try {
    // This is of type AxiosRequestConfig.
    // This is needed because currently we read the auth token in the BFF axios
    // request interceptor from redux state. At this point in time token is not
    // yet set in redux (it is set only after the loginSuccess action is dispatched
    // and reducer is amended). And we want to avoid sending the token in the body
    // as we already have l7-t token header extraction logic on BFF, so we want to
    // utilize it.
    // If we choose to read the token directly from the cookie in the BFF axios request
    // interceptor sometimes in the future, then this manual setting of the l7-t
    // header won't be needed.
    // `l` query param (which we automatically amend to the BFF request via the
    // request interceptor for every GET request) should not matter here.
    const axiosConfig = {
      headers: {
        'l7-t': token,
      },
    };
    const response = yield call(BffApiService.checkSession, {
      config: axiosConfig,
    });

    if (!isAxiosResponse(response)) {
      yield put(autoLoginUpdate(AutoLoginStep.Error));
      return;
    }

    if (!response.data.data) {
      yield put(autoLoginUpdate(AutoLoginStep.Error));
      return;
    }
    const storedIsTimeoutBlocked = localStorage.getItem('isTimeoutBlocked') === 'true';
    yield put(
      loginSuccess(token, {
        isRegistration: false,
        isAutoLogin: true,
        isTimeoutBlocked: storedIsTimeoutBlocked,
      }),
    );
    yield put(autoLoginUpdate(AutoLoginStep.Success));
    triggerGTag(GTagEvents.login_auto);
  } catch (error) {
    yield put(autoLoginUpdate(AutoLoginStep.Error));
  }
}

// TODO: rename this to watchAuth once we finish the auth redux refactor to TS
export function* watchAutoLogin(): Generator<AllEffect<ForkEffect<never>>, void> {
  yield all([
    takeEvery(AutoLoginActionType.AutoLogin, autoLoginSaga),
    takeEvery(AutoLoginActionType.RefreshUser, refreshUserSaga),
  ]);
}
